import { useState, useEffect } from "react";
import axios from "axios";
import {
  Tag, Plus, Trash2, Check, ToggleLeft, ToggleRight, Users, ShieldCheck
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPromos() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    code: "", discount_type: "percentage", discount_value: 10,
    max_uses: 100, bonus_scans: 0, expires_at: "",
    usage_restriction: "any", max_uses_per_user: 0, first_n_users: 0,
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => { fetchPromos(); }, []);

  const fetchPromos = async () => {
    try {
      const res = await axios.get(`${API}/admin/promos`);
      setPromos(res.data);
    } catch { toast.error("Failed to load promos"); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.code.trim()) { toast.error("Code is required"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/promos`, form);
      setPromos(prev => [res.data, ...prev]);
      setShowCreate(false);
      setForm({ code: "", discount_type: "percentage", discount_value: 10, max_uses: 100, bonus_scans: 0, expires_at: "", usage_restriction: "any", max_uses_per_user: 0, first_n_users: 0 });
      toast.success("Promo created!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create"); }
    finally { setCreating(false); }
  };

  const handleToggle = async (promo) => {
    try {
      const res = await axios.patch(`${API}/admin/promos/${promo.id}`, { active: !promo.active });
      setPromos(prev => prev.map(p => p.id === promo.id ? res.data : p));
      toast.success(res.data.active ? "Promo activated" : "Promo deactivated");
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/promos/${id}`);
      setPromos(prev => prev.filter(p => p.id !== id));
      toast.success("Promo deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const formatDate = (iso) => {
    if (!iso) return "Never";
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return iso; }
  };

  const restrictionLabel = (r) => {
    if (r === "first_purchase") return "First purchase only";
    if (r === "first_n_transactions") return "Limited per user";
    return "Any purchase";
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: "4rem" }}><div className="processing-spinner" /></div>;
  }

  return (
    <div className="page-container fade-in" data-testid="admin-promos-page">
      <div className="page-header">
        <div><h1>Promo Codes</h1><p className="page-subtitle">Create and manage promotional codes for discounts.</p></div>
        <button className="dash-cta-btn" onClick={() => setShowCreate(!showCreate)} data-testid="admin-create-promo-btn">
          <Plus size={16} /> Create Promo
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="admin-create-promo fade-in" data-testid="admin-promo-form">
          <div className="modal-row">
            <div className="modal-field">
              <label>Code *</label>
              <input className="modal-input" placeholder="e.g. LAUNCH20" value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="promo-code-input" />
            </div>
            <div className="modal-field">
              <label>Discount Type</label>
              <select className="modal-input" value={form.discount_type}
                onChange={e => setForm({ ...form, discount_type: e.target.value })} data-testid="promo-type-select">
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
          </div>
          <div className="modal-row">
            <div className="modal-field">
              <label>Discount Value ({form.discount_type === "percentage" ? "%" : "$"})</label>
              <input className="modal-input" type="number" value={form.discount_value}
                onChange={e => setForm({ ...form, discount_value: e.target.value })} data-testid="promo-value-input" />
            </div>
            <div className="modal-field">
              <label>Total Max Uses (0 = unlimited)</label>
              <input className="modal-input" type="number" value={form.max_uses}
                onChange={e => setForm({ ...form, max_uses: e.target.value })} data-testid="promo-max-uses" />
            </div>
          </div>
          <div className="modal-row">
            <div className="modal-field">
              <label>Usage Restriction</label>
              <select className="modal-input" value={form.usage_restriction}
                onChange={e => setForm({ ...form, usage_restriction: e.target.value })} data-testid="promo-restriction-select">
                <option value="any">Any purchase</option>
                <option value="first_purchase">First purchase only</option>
                <option value="first_n_transactions">First N transactions per user</option>
              </select>
            </div>
            {form.usage_restriction === "first_n_transactions" && (
              <div className="modal-field">
                <label>Max Uses Per User</label>
                <input className="modal-input" type="number" value={form.max_uses_per_user}
                  onChange={e => setForm({ ...form, max_uses_per_user: e.target.value })} data-testid="promo-per-user" />
              </div>
            )}
          </div>
          <div className="modal-row">
            <div className="modal-field">
              <label>First N Users (0 = no limit)</label>
              <input className="modal-input" type="number" value={form.first_n_users} placeholder="e.g. 100 (first 100 users)"
                onChange={e => setForm({ ...form, first_n_users: e.target.value })} data-testid="promo-first-n-users" />
            </div>
            <div className="modal-field">
              <label>Bonus Scans (0 = none)</label>
              <input className="modal-input" type="number" value={form.bonus_scans}
                onChange={e => setForm({ ...form, bonus_scans: e.target.value })} data-testid="promo-bonus-scans" />
            </div>
          </div>
          <div className="modal-row">
            <div className="modal-field">
              <label>Expires At (optional)</label>
              <input className="modal-input" type="date" value={form.expires_at}
                onChange={e => setForm({ ...form, expires_at: e.target.value })} data-testid="promo-expires" />
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
            <button className="dash-cta-btn" onClick={handleCreate} disabled={creating} data-testid="promo-save-btn">
              <Check size={14} /> {creating ? "Creating..." : "Create Promo"}
            </button>
            <button className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Promo List */}
      {promos.length === 0 ? (
        <div className="dash-empty-card" data-testid="admin-promos-empty">
          <div className="dash-empty-icon"><Tag size={28} /></div>
          <h3>No promo codes yet</h3>
          <p>Create your first promotional code.</p>
        </div>
      ) : (
        <div className="admin-table-wrap" data-testid="admin-promos-table">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Restriction</th>
                <th>Uses</th>
                <th>Unique Users</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {promos.map(p => (
                <tr key={p.id} data-testid={`promo-row-${p.id}`}>
                  <td><code className="admin-promo-code">{p.code}</code></td>
                  <td>{p.discount_type === "percentage" ? `${p.discount_value}%` : `$${p.discount_value}`}</td>
                  <td>
                    <span className="admin-promo-restriction">
                      <ShieldCheck size={12} />
                      {restrictionLabel(p.usage_restriction)}
                      {p.first_n_users > 0 && <span className="admin-promo-sub"> (first {p.first_n_users} users)</span>}
                    </span>
                  </td>
                  <td>{p.used_count} / {p.max_uses || "unlimited"}</td>
                  <td>
                    <span className="admin-promo-users">
                      <Users size={12} />
                      {(p.used_by_users ? new Set(p.used_by_users).size : 0)}
                    </span>
                  </td>
                  <td>{formatDate(p.expires_at)}</td>
                  <td>
                    <button className={`admin-toggle-btn ${p.active ? "active" : ""}`}
                      onClick={() => handleToggle(p)} data-testid={`promo-toggle-${p.id}`}>
                      {p.active ? <><ToggleRight size={16} /> Active</> : <><ToggleLeft size={16} /> Inactive</>}
                    </button>
                  </td>
                  <td>
                    <button className="btn-icon danger" onClick={() => handleDelete(p.id)} data-testid={`promo-delete-${p.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
